import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { CustomPaginator, GenericTable } from '@cdaas/reusable-components-daas';
import { omit } from 'lodash';
import { UserListUser } from '@cdaas/api-clients/dist/asset-user-openapi';

import { getAllUsers, getUserGroups } from '../../../store/actions';
import { RootState } from '../../../store/reducers';
import { UserGroup } from '../../../types';
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants';

enum SortableColumns {
  Name,
  UserCount,
}

const UserGroupsList = () => {
  const { t } = useTranslation('editOrganization');
  const dispatch = useDispatch();

  const loading = useSelector((state: RootState) => state.userGroups.loading);
  const userGroups: UserGroup[] = useSelector((state: RootState) => state.userGroups.userGroups, shallowEqual);
  const users = useSelector((state: RootState) => state.users.users);
  const isAuthenticated: boolean = useSelector((state: RootState) => state.authentication.authenticated);

  const [userGroupsWithUsers, setUserGroupsWithUsers] = useState<UserGroup[]>([]);
  const [userGroupsPage, setUserGroupsPage] = useState<UserGroup[]>([]);

  const [currentSort, setCurrentSort] = useState<SortableColumns>();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserGroups());
      dispatch(getAllUsers());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    setUserGroupsWithUsers(() =>
      userGroups.map(userGroup => ({
        ...userGroup,
        users: users
          .filter(user => user.user_group?.id === userGroup.id)
          .map(user => omit(user, ['allowance', 'allowance_type', 'policies'])) as UserListUser[],
      })),
    );
  }, [userGroups, users]);

  const handleSortChange = (sortColumn: SortableColumns) => {
    const sortedUserGroupsWithUsers = [...userGroupsWithUsers];
    if (sortColumn === currentSort) {
      sortedUserGroupsWithUsers.reverse();
    } else {
      if (sortColumn === SortableColumns.Name) {
        sortedUserGroupsWithUsers.sort((a, b) => (a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
      } else if (sortColumn === SortableColumns.UserCount) {
        sortedUserGroupsWithUsers.sort((a, b) => (a.users && b.users && a.users?.length < b.users?.length ? -1 : 1));
      }
    }
    setCurrentSort(sortColumn);
    setUserGroupsWithUsers(sortedUserGroupsWithUsers);
    const currentPageUserGroups = sortedUserGroupsWithUsers.slice(0, DEFAULT_PAGE_SIZE);
    setUserGroupsPage(currentPageUserGroups);
  };

  return (
    <>
      <GenericTable
        loading={loading ? 'short' : 'none'}
        noDataText={t('UserGroups.noUserGroupsMessage')}
        inputData={userGroupsPage}
        rowConfigs={[
          {
            header: t('UserGroups.userGroupsTable.nameHeader'),
            key: 'name',
            fullyExternalSortFunction: () => handleSortChange(SortableColumns.Name),
          },
          {
            header: t('UserGroups.userGroupsTable.usersHeader'),
            formatValue: (_keyValue, row) => `${row.users?.length} ${t('UserGroups.userGroupsTable.usersHeader').toLocaleLowerCase()}`,
            fullyExternalSortFunction: () => handleSortChange(SortableColumns.UserCount),
          },
        ]}
        // Commented out because of yet missing functionality
        // buttonConfig={{
        //   name: t('UserGroups.userGroupsTable.buttonText'),
        //   buttonClickCallback: () => undefined,
        //   buttonProps: {
        //     iconOnRight: true,
        //     iconName: 'chevron-right',
        //     variant: 'secondary'
        //   }
        // }}
      />
      <CustomPaginator
        activateInputData={{
          inputData: userGroupsWithUsers,
          pageSize: DEFAULT_PAGE_SIZE,
        }}
        onPageChange={pageChangeInfo => setUserGroupsPage(pageChangeInfo.nextPageItems)}
        paginatorStyleProps={{
          paginationProps: {},
          pageSelectorProps: { centered: true },
        }}
      />
    </>
  );
};

export default UserGroupsList;
